import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FaArrowLeft, FaProjectDiagram, FaMapMarkerAlt, FaCalendarAlt, FaEdit,
  FaFileInvoiceDollar, FaInfoCircle, FaUser, FaPlus, FaBuilding, FaBriefcase,
  FaTasks, FaCheckCircle, FaChevronDown, FaFileAlt, FaFilePdf, FaPaperPlane,
  FaClipboardCheck, FaCheck,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import ProjectService, { Project, ProjectStatus, PROJECT_STATUS_OPTIONS } from '../services/ProjectService';
import BusinessPartnerService from '../services/BusinessPartnerService';
import ProjectModal from './ProjectModal';
import OffersTab from './OffersTab';
import PlantafelService, { ProjectSite, ProjectJobShift, ProjectMilestone, MilestoneJob } from '../../operations/services/PlantafelService';
import OfferService, { Offer } from '../../sales/services/OfferService';
import CrmTaskService, { CrmTask } from '../../sales/services/CrmTaskService';
import SiteModal from '../../operations/components/SiteModal';
import ApplyTemplateModal from '../../operations/components/ApplyTemplateModal';
import CreateOfferFromTemplateModal from '../../sales/components/CreateOfferFromTemplateModal';
import { ProjectWorkflowTimeline } from './ProjectWorkflowTimeline';
import { ProjectOverviewTab } from './ProjectOverviewTab';
import { ProjectSitesTab } from './ProjectSitesTab';
import { ProjectJobsTab } from './ProjectJobsTab';
import { ProjectMilestonesTab } from './ProjectMilestonesTab';
import { GenerateJobsDialog, InvoiceConfirmDialog } from './ProjectConfirmDialogs';
import { formatDateDE } from './projectDetailUtils';
import styles from './ProjectDetailPage.module.css';

type TabKey = 'overview' | 'sites' | 'jobs' | 'milestones' | 'offers';
const tabDefs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'overview', label: 'Overview', icon: <FaInfoCircle /> },
  { key: 'sites', label: 'Sites', icon: <FaBuilding /> },
  { key: 'jobs', label: 'Jobs', icon: <FaBriefcase /> },
  { key: 'milestones', label: 'Milestones', icon: <FaTasks /> },
  { key: 'offers', label: 'Offers', icon: <FaFileInvoiceDollar /> },
];

export default function ProjectDetailPage(): React.JSX.Element {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const pid = projectId ? parseInt(projectId, 10) : 0;

  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [project, setProject] = useState<Project | null>(null);
  const [partnerName, setPartnerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [sites, setSites] = useState<ProjectSite[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<ProjectSite | null>(null);
  const [deletingSiteId, setDeletingSiteId] = useState<number | null>(null);

  const [jobs, setJobs] = useState<ProjectJobShift[]>([]);
  const [backlogTasks, setBacklogTasks] = useState<CrmTask[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  const [milestones, setMilestones] = useState<ProjectMilestone[]>([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [expandedMilestones, setExpandedMilestones] = useState<Set<number>>(new Set());

  const [applyTemplateModalOpen, setApplyTemplateModalOpen] = useState(false);
  const [generateJobsConfirmOpen, setGenerateJobsConfirmOpen] = useState(false);
  const [generateJobsMilestone, setGenerateJobsMilestone] = useState<ProjectMilestone | null>(null);
  const [generatingJobs, setGeneratingJobs] = useState(false);

  const [milestonesOfferId, setMilestonesOfferId] = useState<number | null>(null);
  const [invoiceConfirmOpen, setInvoiceConfirmOpen] = useState(false);
  const [invoiceMilestone, setInvoiceMilestone] = useState<ProjectMilestone | null>(null);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [openingInvoiceMilestoneId, setOpeningInvoiceMilestoneId] = useState<number | null>(null);

  const [primaryOffer, setPrimaryOffer] = useState<Offer | null>(null);
  const [offerFromTemplateModalOpen, setOfferFromTemplateModalOpen] = useState(false);

  const [draggedJobId, setDraggedJobId] = useState<number | null>(null);
  const [dragOverMilestoneId, setDragOverMilestoneId] = useState<number | 'unassigned' | null>(null);
  const [reassigningJobId, setReassigningJobId] = useState<number | null>(null);

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const statusDropdownRef = React.useRef<HTMLDivElement>(null);
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
  const [offerDropdownOpen, setOfferDropdownOpen] = useState(false);
  const actionsDropdownRef = React.useRef<HTMLDivElement>(null);
  const offerDropdownRef = React.useRef<HTMLDivElement>(null);

  /* ── Effects ── */
  useEffect(() => {
    const h = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) setStatusDropdownOpen(false);
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) setActionsDropdownOpen(false);
      if (offerDropdownRef.current && !offerDropdownRef.current.contains(event.target as Node)) setOfferDropdownOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { if (pid) loadProject(pid); }, [projectId]);
  useEffect(() => { if (activeTab === 'sites' && pid) loadSites(pid); }, [activeTab, projectId]);
  useEffect(() => { if (activeTab === 'jobs' && pid) loadJobs(pid); }, [activeTab, projectId]);
  useEffect(() => { if (activeTab === 'milestones' && pid) loadMilestones(pid); }, [activeTab, projectId]);

  useEffect(() => {
    const h = ((event: CustomEvent<{ projectId: number }>) => {
      if (event.detail?.projectId && pid && event.detail.projectId === pid) loadMilestones(pid);
    }) as EventListener;
    window.addEventListener('milestoneStatusChanged', h);
    return () => window.removeEventListener('milestoneStatusChanged', h);
  }, [projectId]);

  /* ── Loaders ── */
  const loadSites = async (id: number) => {
    setSitesLoading(true);
    try { setSites(await PlantafelService.fetchProjectSites(id)); }
    catch (err) { console.error('Failed to load sites:', err); }
    finally { setSitesLoading(false); }
  };

  const loadJobs = async (id: number) => {
    setJobsLoading(true);
    try {
      const [scheduled, tasks] = await Promise.all([PlantafelService.fetchProjectJobShifts(id), CrmTaskService.list({ project_id: id })]);
      setJobs(scheduled);
      const scheduledIds = new Set(scheduled.map((j) => j.crm_task_id).filter(Boolean));
      setBacklogTasks(tasks.filter((t) => !scheduledIds.has(t.id)));
    } catch (err) { console.error('Failed to load jobs:', err); }
    finally { setJobsLoading(false); }
  };

  const loadMilestones = async (id: number) => {
    setMilestonesLoading(true);
    try { const r = await PlantafelService.fetchProjectMilestonesWithMeta(id, true); setMilestones(r.milestones); setMilestonesOfferId(r.offer_id); }
    catch (err) { console.error('Failed to load milestones:', err); }
    finally { setMilestonesLoading(false); }
  };

  const loadProject = async (id: number) => {
    setLoading(true);
    try {
      const data = await ProjectService.get(id);
      setProject(data);
      if (data.partner) {
        try { const p = await BusinessPartnerService.get(data.partner); setPartnerName(BusinessPartnerService.getDisplayName(p)); }
        catch { setPartnerName(data.partner_name || ''); }
      }
      try { const offers = await OfferService.list({ project_id: id }); const acc = offers.find((o) => ['ACCEPTED', 'INVOICED'].includes(o.status?.toUpperCase() || '')); const sent = offers.find((o) => o.status?.toUpperCase() === 'SENT'); setPrimaryOffer(acc || sent || offers[0] || null); }
      catch { setPrimaryOffer(null); }
      try { const r = await PlantafelService.fetchProjectMilestonesWithMeta(id, true); setMilestones(r.milestones); setMilestonesOfferId(r.offer_id); }
      catch { /* silent */ }
    } catch (err) { console.error('Failed to load project:', err); toast.error('Failed to load project'); navigate('/contacts'); }
    finally { setLoading(false); }
  };

  /* ── Handlers ── */
  const handleBack = () => { project?.partner ? navigate(`/contacts/${project.partner}`) : navigate('/contacts'); };
  const handleEditSave = () => { setEditModalOpen(false); if (pid) loadProject(pid); };
  const handleCreateOffer = () => { if (project) navigate(`/offers/new?partner_id=${project.partner}&project_id=${project.id}`); };
  const handleCreateSite = () => { setEditingSite(null); setSiteModalOpen(true); };
  const handleEditSite = (site: ProjectSite) => { setEditingSite(site); setSiteModalOpen(true); };
  const handleSiteModalSave = () => { if (pid) loadSites(pid); };
  const handleOpenJobInPlantafel = (job: ProjectJobShift) => navigate(`/operations?date=${job.start_time.split('T')[0]}&shiftId=${job.id}`);

  const handleDeleteSite = async (siteId: number) => {
    if (!window.confirm('Site wirklich löschen?')) return;
    setDeletingSiteId(siteId);
    try { await PlantafelService.deleteProjectSite(siteId); toast.success('Site gelöscht'); setSites((p) => p.filter((s) => s.id !== siteId)); }
    catch (err) { console.error('Failed to delete site:', err); toast.error('Fehler beim Löschen'); }
    finally { setDeletingSiteId(null); }
  };

  const handleChangeStatus = async (newStatus: ProjectStatus) => {
    if (!project || project.status === newStatus) { setStatusDropdownOpen(false); return; }
    setChangingStatus(true);
    try { await ProjectService.update(project.id, { status: newStatus }); toast.success(`Status auf "${ProjectService.getStatusLabel(newStatus)}" geändert`); setStatusDropdownOpen(false); loadProject(project.id); }
    catch (err) { console.error('Failed to change status:', err); toast.error('Status konnte nicht geändert werden'); }
    finally { setChangingStatus(false); }
  };

  const toggleMilestoneExpand = (id: number) => setExpandedMilestones((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const handleOpenMilestoneJobInPlantafel = (job: MilestoneJob) => {
    if (job.start_time) { const d = job.start_time.split('T')[0]; navigate(`/operations?date=${d}${job.shift_id ? `&shiftId=${job.shift_id}` : ''}`); }
    else navigate('/operations?tab=backlog');
  };

  const handleGenerateJobs = async () => {
    if (!generateJobsMilestone || !project) return;
    setGeneratingJobs(true);
    try {
      const result = await PlantafelService.generateJobsFromMilestone(project.id, generateJobsMilestone.id);
      setGenerateJobsConfirmOpen(false); setGenerateJobsMilestone(null);
      toast.success(<div>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>{result.created_tasks_count} Job{result.created_tasks_count !== 1 ? 's' : ''} erstellt</div>
        {result.existing_tasks_count > 0 && <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>{result.existing_tasks_count} Job{result.existing_tasks_count !== 1 ? 's' : ''} übersprungen (bereits vorhanden)</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button onClick={() => navigate('/operations?tab=backlog')} style={{ padding: '4px 12px', border: '1px solid #7c3aed', borderRadius: 4, background: 'white', color: '#7c3aed', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>Zum Backlog</button>
          <button onClick={() => navigate('/operations')} style={{ padding: '4px 12px', border: 'none', borderRadius: 4, background: '#7c3aed', color: 'white', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>Zur Plantafel</button>
        </div>
      </div>, { autoClose: 10000 });
      loadMilestones(project.id);
    } catch (err: any) { console.error('Failed to generate jobs:', err); toast.error(err?.response?.data?.detail || 'Fehler beim Erzeugen der Jobs'); }
    finally { setGeneratingJobs(false); }
  };

  const handleCreateInvoiceDraft = async () => {
    if (!invoiceMilestone || !milestonesOfferId || !project) return;
    setCreatingInvoice(true);
    try {
      const r = await OfferService.createInvoiceDraftFromMilestone(milestonesOfferId, invoiceMilestone.id);
      setInvoiceConfirmOpen(false); setInvoiceMilestone(null);
      const existing = r.message === 'invoice_already_exists';
      toast.success(<div>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>{existing ? 'Rechnung bereits vorhanden' : 'Rechnung erstellt'}: {r.invoice_number} (Entwurf)</div>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>{r.positions_count} Leistung{r.positions_count !== 1 ? 'en' : ''} • {new Intl.NumberFormat('de-DE', { style: 'currency', currency: r.currency }).format(r.amount_net)} netto</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button onClick={() => navigate(`/invoices/${r.invoice_id}`)} style={{ padding: '6px 14px', border: 'none', borderRadius: 4, background: '#021a4a', color: 'white', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>Zur Rechnung</button>
          <button onClick={() => toast.dismiss()} style={{ padding: '6px 14px', border: '1px solid #e0e3ef', borderRadius: 4, background: 'white', color: '#666', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>Schließen</button>
        </div>
      </div>, { autoClose: 15000 });
      loadMilestones(project.id);
    } catch (err: any) {
      console.error('Failed to create invoice draft:', err);
      const st = err?.response?.status, ed = err?.response?.data;
      if (st === 409) { if (ed?.error === 'milestone_not_ready') { toast.warning('Milestone ist nicht bereit zur Rechnungsstellung'); if (project) loadMilestones(project.id); } else if (ed?.error === 'no_positions_assigned') toast.error('Keine Leistungen zugeordnet'); else toast.error(ed?.detail || 'Konflikt beim Erstellen der Rechnung'); }
      else if (st === 404) toast.error('Milestone oder Angebot nicht gefunden');
      else toast.error(ed?.detail || 'Fehler beim Erstellen der Rechnung');
    } finally { setCreatingInvoice(false); }
  };

  const handleOpenExistingInvoice = async (milestone: ProjectMilestone) => {
    if (!milestonesOfferId) return;
    setOpeningInvoiceMilestoneId(milestone.id);
    try { const r = await OfferService.createInvoiceDraftFromMilestone(milestonesOfferId, milestone.id); navigate(`/invoices/${r.invoice_id}`); }
    catch (err: any) {
      console.error('Failed to open invoice:', err);
      const st = err?.response?.status, ed = err?.response?.data;
      if (st === 409) { if (ed?.error === 'milestone_not_ready') { toast.warning('Milestone-Status hat sich geändert'); if (project) loadMilestones(project.id); } else if (ed?.error === 'no_positions_assigned') toast.error('Keine Leistungen zugeordnet'); else toast.error(ed?.detail || 'Fehler beim Öffnen der Rechnung'); }
      else if (st === 404) toast.error('Rechnung nicht gefunden');
      else toast.error(ed?.detail || 'Fehler beim Öffnen der Rechnung');
    } finally { setOpeningInvoiceMilestoneId(null); }
  };

  /* ── DnD ── */
  const handleJobDragStart = (e: React.DragEvent, jobId: number) => {
    if (!Number.isFinite(jobId)) { console.warn('[Job DnD] Invalid ID:', jobId); e.preventDefault(); return; }
    setDraggedJobId(jobId); e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-job-task-id', String(jobId)); e.dataTransfer.setData('text/plain', String(jobId));
  };
  const handleJobDragEnd = () => { setDraggedJobId(null); setDragOverMilestoneId(null); };
  const handleMilestoneJobDragOver = (e: React.DragEvent, mid: number | 'unassigned') => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverMilestoneId(mid); };
  const handleMilestoneJobDragLeave = () => setDragOverMilestoneId(null);
  const handleMilestoneJobDrop = async (e: React.DragEvent, targetId: number | null) => {
    e.preventDefault(); setDragOverMilestoneId(null);
    const s = e.dataTransfer.getData('application/x-job-task-id') || e.dataTransfer.getData('text/plain');
    if (!s) { toast.error('Drag-Daten ungültig (keine Job-ID)'); return; }
    const id = Number(s); if (!Number.isFinite(id)) { toast.error('Drag-Daten ungültig (Job-ID ist keine Zahl)'); return; }
    await handleJobMilestoneChange(id, targetId); setDraggedJobId(null);
  };

  const handleJobMilestoneChange = async (jobId: number | undefined, milestoneId: number | null) => {
    if (!pid) return;
    if (!Number.isFinite(jobId)) { toast.error('Verschieben fehlgeschlagen: Ungültige Job-ID'); return; }
    if (milestoneId !== null && !Number.isFinite(milestoneId)) { toast.error('Verschieben fehlgeschlagen: Ungültige Milestone-ID'); return; }
    let cur: number | null = null;
    for (const m of milestones) { if (m.jobs?.some((j) => j.id === jobId)) { cur = m.id; break; } }
    if (cur === milestoneId) return;
    setReassigningJobId(jobId!);
    const prev = [...milestones];
    setMilestones((ms) => ms.map((m) => {
      if (m.jobs?.some((j) => j.id === jobId)) return { ...m, jobs: m.jobs?.filter((j) => j.id !== jobId) };
      if (m.id === milestoneId) { const job = prev.flatMap((pm) => pm.jobs || []).find((j) => j.id === jobId); if (job) return { ...m, jobs: [...(m.jobs || []), job] }; }
      return m;
    }));
    try {
      if (process.env.NODE_ENV === 'development') console.log('[Job milestone-assign] PATCH:', { jobId, payment_milestone_id: milestoneId });
      await PlantafelService.updateJobMilestone(jobId!, milestoneId); toast.success('Job verschoben'); loadMilestones(pid);
    } catch (err: any) {
      const d = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Unbekannter Fehler';
      const c = err?.response?.data?.code;
      toast.error(c ? `Verschieben fehlgeschlagen: ${d} (code=${c})` : `Verschieben fehlgeschlagen: ${d}`);
      setMilestones(prev);
    } finally { setReassigningJobId(null); }
  };

  /* ── Render guards ── */
  if (loading) return <div className={styles.loadingState}>Loading...</div>;
  if (!project) return <div className={styles.loadingState}>Project not found</div>;

  const statusColor = ProjectService.getStatusColor(project.status);
  const addr = [project.street, project.house_no].filter(Boolean).join(' ');
  const city = [project.postal_code, project.city].filter(Boolean).join(' ');
  const fullAddress = [addr, city].filter(Boolean).join(', ');

  const offerNav = primaryOffer ? () => navigate(`/offers/${primaryOffer.id}`) : undefined;
  const oSt = primaryOffer?.status?.toUpperCase() || '';
  const isSent = ['SENT', 'ACCEPTED', 'REJECTED', 'INVOICED'].includes(oSt);
  const isAccepted = ['ACCEPTED', 'INVOICED'].includes(oSt);
  const hasAB = !!primaryOffer?.order_confirmation_number;
  const tM = milestones.length;
  const iM = milestones.filter((m) => m.invoice_id || m.status === 'INVOICED' || m.status === 'PAID').length;
  const anyInv = iM > 0, allInv = iM === tM && tM > 0;
  const workflowSteps = [
    { id: 'created', actionLabel: 'Erstellen', completedLabel: 'Erstellt', completed: true, icon: <FaFilePdf size={18} color="#fff" />, completedAt: project.created_at, onCompletedClick: offerNav },
    { id: 'sent', actionLabel: 'Versenden', completedLabel: 'Versendet', completed: isSent, disabled: !primaryOffer, icon: <FaPaperPlane size={18} color={isSent ? '#fff' : '#999'} />, completedAt: primaryOffer?.sent_at, onCompletedClick: offerNav },
    { id: 'accepted', actionLabel: 'Akzeptieren', completedLabel: 'Akzeptiert', completed: isAccepted, disabled: !isSent, icon: <FaCheck size={18} color={isAccepted ? '#fff' : '#999'} />, completedAt: primaryOffer?.accepted_at, onCompletedClick: offerNav },
    { id: 'ab', actionLabel: 'AB senden', completedLabel: 'AB gesendet', completed: hasAB, disabled: !isAccepted, show: isAccepted || hasAB, icon: <FaClipboardCheck size={18} color={hasAB ? '#fff' : '#999'} />, completedAt: primaryOffer?.order_confirmed_at, onCompletedClick: offerNav },
    { id: 'invoice', actionLabel: tM > 0 ? `Rechnungen (0/${tM})` : 'Rechnung', completedLabel: allInv ? `Alle Rechnungen (${tM}/${tM})` : `Rechnungen (${iM}/${tM})`, completed: allInv, disabled: !hasAB && !anyInv, show: isAccepted || hasAB || anyInv, icon: <FaFileInvoiceDollar size={18} color={allInv || anyInv ? '#fff' : '#999'} />, partialProgress: anyInv && !allInv ? `${iM}/${tM}` : undefined, onCompletedClick: offerNav },
  ];

  return (
    <div className={styles.pageWrapper}>
      <button onClick={handleBack} className={styles.backButton}><FaArrowLeft size={14} /> Back to contact</button>

      <div className={styles.headerCard}>
        <div className={styles.headerRow}>
          <div className={styles.headerLeft}>
            <div className={styles.avatar}><FaProjectDiagram size={40} color="#021a4a" /></div>
            <div className={styles.infoCol}>
              <div className={styles.nameRow}>
                <h1 className={styles.projectName}>{project.name}</h1>
                <div ref={statusDropdownRef} className={styles.statusDropdownWrapper}>
                  <button onClick={() => setStatusDropdownOpen(!statusDropdownOpen)} disabled={changingStatus} className={styles.statusButton} style={{ background: statusColor.bg, color: statusColor.text, cursor: changingStatus ? 'wait' : 'pointer' }}>
                    {ProjectService.getStatusLabel(project.status)}
                    <FaChevronDown size={10} className={styles.chevronTransition} style={{ transform: statusDropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
                  </button>
                  {statusDropdownOpen && (
                    <div className={styles.dropdownMenu}>
                      {PROJECT_STATUS_OPTIONS.map((opt) => {
                        const cur = project.status === opt.value, oc = ProjectService.getStatusColor(opt.value);
                        return (
                          <button key={opt.value} onClick={() => handleChangeStatus(opt.value)} className={styles.statusOption} style={{ background: cur ? '#f3f4f6' : 'white', cursor: cur ? 'default' : 'pointer' }}>
                            <span className={styles.statusDot} style={{ background: oc.bg, border: `2px solid ${oc.text}` }} />
                            {ProjectService.getStatusLabel(opt.value)}
                            {cur && <FaCheckCircle size={12} color="#059669" style={{ marginLeft: 'auto' }} />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.metaRow}>
                {project.reference && <div className={styles.metaItem}><span className={styles.metaText}><strong>Ref:</strong> {project.reference}</span></div>}
                {partnerName && <div className={styles.metaItem}><FaUser color="#666" size={14} /><span className={styles.metaTextDark}>{project.partner ? <Link to={`/contacts/${project.partner}`} className={styles.partnerLink}>{partnerName}</Link> : partnerName}</span></div>}
              </div>
              {fullAddress && <div className={styles.metaRowSmall}><div className={styles.metaItem}><FaMapMarkerAlt color="#666" size={14} /><span className={styles.metaText}>{fullAddress}</span></div></div>}
              <div className={styles.metaRowSmall}>{(project.start_date || project.end_date) && <div className={styles.metaItem}><FaCalendarAlt color="#666" size={14} /><span className={styles.metaText}>{formatDateDE(project.start_date)} - {formatDateDE(project.end_date)}</span></div>}</div>
              <div className={styles.createdDate}>Created: {formatDateDE(project.created_at)}</div>
            </div>
          </div>
          <div className={styles.badgesCol}>
            <div className={styles.badgesRow}>
              {project.priority && project.priority !== 'NORMAL' && <span className={styles.badge} style={{ background: ProjectService.getPriorityColor(project.priority).bg, color: ProjectService.getPriorityColor(project.priority).text }}>{project.priority_display || ProjectService.getPriorityLabel(project.priority)}</span>}
              {project.project_type && <span className={`${styles.badge} ${styles.badgeDefault}`}>{project.project_type_display || ProjectService.getProjectTypeLabel(project.project_type)}</span>}
            </div>
            <div className={styles.actionsRow}>
              <div ref={actionsDropdownRef} style={{ position: 'relative' }}>
                <button onClick={() => setActionsDropdownOpen(!actionsDropdownOpen)} className={styles.actionsButton}><FaEdit size={14} /> Aktionen <FaChevronDown size={10} className={styles.chevronTransition} style={{ transform: actionsDropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }} /></button>
                {actionsDropdownOpen && <div className={styles.dropdownMenuRight}>
                  <button onClick={() => { setEditModalOpen(true); setActionsDropdownOpen(false); }} className={styles.dropdownItem}><FaEdit size={14} color="#666" /> Bearbeiten</button>
                  <button data-testid="apply-template-button" onClick={() => { setApplyTemplateModalOpen(true); setActionsDropdownOpen(false); }} className={styles.dropdownItem}><FaFileAlt size={14} color="#7c3aed" /> Projektplan aus Vorlage</button>
                </div>}
              </div>
              <div ref={offerDropdownRef} style={{ position: 'relative' }}>
                <button data-testid="offer-dropdown-btn" onClick={() => setOfferDropdownOpen(!offerDropdownOpen)} className={styles.offerButton}><FaFileInvoiceDollar size={14} /> Angebot <FaChevronDown size={10} className={styles.chevronTransition} style={{ transform: offerDropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }} /></button>
                {offerDropdownOpen && <div className={styles.dropdownMenuRight}>
                  <button onClick={() => { handleCreateOffer(); setOfferDropdownOpen(false); }} className={styles.dropdownItem}><FaPlus size={14} color="#059669" /> Neues Angebot</button>
                  <button data-testid="create-offer-from-template-btn" onClick={() => { setOfferFromTemplateModalOpen(true); setOfferDropdownOpen(false); }} className={styles.dropdownItem}><FaFileAlt size={14} color="#666" /> Aus Vorlage erstellen</button>
                </div>}
              </div>
            </div>
          </div>
        </div>
        <ProjectWorkflowTimeline steps={workflowSteps} />
      </div>

      <div className={styles.tabsCard}>
        <div className={styles.tabBar}>
          {tabDefs.map((t) => <button key={t.key} data-testid={`project-tab-${t.key}`} onClick={() => setActiveTab(t.key)} className={`${styles.tabButton} ${activeTab === t.key ? styles.tabButtonActive : styles.tabButtonInactive}`}>{t.icon} {t.label}</button>)}
        </div>
        <div className={styles.tabContent}>
          {activeTab === 'overview' && <ProjectOverviewTab project={project} />}
          {activeTab === 'sites' && <ProjectSitesTab sites={sites} sitesLoading={sitesLoading} deletingSiteId={deletingSiteId} onCreateSite={handleCreateSite} onEditSite={handleEditSite} onDeleteSite={handleDeleteSite} />}
          {activeTab === 'jobs' && <ProjectJobsTab jobs={jobs} backlogTasks={backlogTasks} jobsLoading={jobsLoading} milestones={milestones} projectName={project.name} onOpenJobInPlantafel={handleOpenJobInPlantafel} onNavigateToPlantafel={() => navigate('/plantafel')} />}
          {activeTab === 'milestones' && <ProjectMilestonesTab milestones={milestones} milestonesLoading={milestonesLoading} milestonesOfferId={milestonesOfferId} expandedMilestones={expandedMilestones} draggedJobId={draggedJobId} dragOverMilestoneId={dragOverMilestoneId} reassigningJobId={reassigningJobId} openingInvoiceMilestoneId={openingInvoiceMilestoneId} onToggleMilestoneExpand={toggleMilestoneExpand} onOpenMilestoneJobInPlantafel={handleOpenMilestoneJobInPlantafel} onSetInvoiceMilestone={setInvoiceMilestone} onSetInvoiceConfirmOpen={setInvoiceConfirmOpen} onOpenExistingInvoice={handleOpenExistingInvoice} onSetGenerateJobsMilestone={setGenerateJobsMilestone} onSetGenerateJobsConfirmOpen={setGenerateJobsConfirmOpen} onJobDragStart={handleJobDragStart} onJobDragEnd={handleJobDragEnd} onMilestoneJobDragOver={handleMilestoneJobDragOver} onMilestoneJobDragLeave={handleMilestoneJobDragLeave} onMilestoneJobDrop={handleMilestoneJobDrop} onJobMilestoneChange={handleJobMilestoneChange} />}
          {activeTab === 'offers' && <OffersTab projectId={project.id} businessPartnerId={project.partner} />}
        </div>
      </div>

      {project && <ProjectModal isOpen={editModalOpen} project={project} partnerId={project.partner} onClose={() => setEditModalOpen(false)} onSave={handleEditSave} />}
      {project && <SiteModal isOpen={siteModalOpen} site={editingSite} projectId={project.id} onClose={() => { setSiteModalOpen(false); setEditingSite(null); }} onSave={handleSiteModalSave} />}
      {project && <ApplyTemplateModal isOpen={applyTemplateModalOpen} projectId={project.id} projectName={project.name} onClose={() => setApplyTemplateModalOpen(false)} onSuccess={() => { if (activeTab === 'milestones') loadMilestones(project.id); }} />}
      {project && <CreateOfferFromTemplateModal isOpen={offerFromTemplateModalOpen} partnerId={project.partner} projectId={project.id} onClose={() => setOfferFromTemplateModalOpen(false)} onCreated={() => { setOfferFromTemplateModalOpen(false); setActiveTab('offers'); }} />}
      {generateJobsConfirmOpen && generateJobsMilestone && <GenerateJobsDialog milestone={generateJobsMilestone} generating={generatingJobs} onConfirm={handleGenerateJobs} onCancel={() => { setGenerateJobsConfirmOpen(false); setGenerateJobsMilestone(null); }} />}
      {invoiceConfirmOpen && invoiceMilestone && <InvoiceConfirmDialog milestone={invoiceMilestone} creating={creatingInvoice} onConfirm={handleCreateInvoiceDraft} onCancel={() => { setInvoiceConfirmOpen(false); setInvoiceMilestone(null); }} />}
    </div>
  );
}
